import { useMemo } from 'react';
import { TransformedLocationData, calculateRouteProgress } from '@features/components/LocationTracking';
import {
  DisplayDataCaseFields,
  buildLocationTrackingDisplayData,
  resolveCaseStatusTrackingConfig,
  checkDeviceGroundNode,
} from '@features/components/TrackingStatusCard';
import { CaseStatus } from '@/api/apis/case';
import { DeviceTracking } from '@/api/apis/composer';

type UseTrackingStatusCardDataParams = {
  status: CaseStatus;
  isOnlyTxCenterProviders: boolean;
  transformedData: TransformedLocationData | null;
  data: DeviceTracking | undefined;
  caseFields: DisplayDataCaseFields;
};

export const useTrackingStatusCardData = ({
  status,
  isOnlyTxCenterProviders,
  transformedData,
  data,
  caseFields,
}: UseTrackingStatusCardDataParams) => {
  const { cardState, title } = useMemo(
    () => resolveCaseStatusTrackingConfig(status, isOnlyTxCenterProviders),
    [status, isOnlyTxCenterProviders],
  );

  const displayData = useMemo(() => {
    const caseArrivalData = data
      ? { caseId: data.caseId, displaySettings: data.displaySettings, arrivalTime: data.arrivalTime }
      : undefined;
    return buildLocationTrackingDisplayData(caseFields, caseArrivalData);
  }, [caseFields, data]);

  const arrivalIsoTime = useMemo<string | null>(() => {
    if (!data) return null;
    if (data.arrivalTime?.eta) return data.arrivalTime.eta;
    return data.arrivalTime?.sta ?? null;
  }, [data]);

  const routeProgress = useMemo(
    () => (transformedData ? calculateRouteProgress(transformedData.devicePolylines, arrivalIsoTime) : null),
    [transformedData, arrivalIsoTime],
  );

  const isGroundLeg = useMemo(() => checkDeviceGroundNode(transformedData), [transformedData]);

  return { cardState, title, displayData, routeProgress, isGroundLeg };
};
